import React from "react";
import useTheme from "@mui/styles/useTheme";

type Translate = (key: string) => string;

type WidgetTemplateFieldValue = string | number | undefined;

export const WIDGET_CHART = (t: Translate) => ({
  area: { value: "area", label: t("Area graph") },
  pie: { value: "pie", label: t("Pie chart") },
  bar: { value: "bar", label: t("Bar chart") },
  line: { value: "line", label: t("Line Graph") },
  histogram: { value: "histogram", label: t("Histogram") },
});

export const WIDGET_TEMPLATE_FIELDS = {
  "categorical-area": {
    location: "the current place name",
    startDataset: "the first dataset in the chart range",
    endDataset: "the last dataset in the chart range",
  },
  "categorical-pie": {
    location: "the current place name",
    // year: "the chart year",
  },
  "categorical-bar": {
    location: "the current place name",
    // year: "the chart year",
  },
  "continuous-area": {
    location: "the current place name",
    startDataset: "the first dataset in the chart range",
    endDataset: "the last dataset in the chart range",
  },
  "continuous-line": {
    location: "the current place name",
    startDataset: "the first dataset in the chart range",
    endDataset: "the last dataset in the chart range",
    numDatasets: "the number of datasets in the chart",
    condition: "the trend direction (upwards or downwards)",
    // percentageChange: "the calculated percentage change across the trend line",
  },
  "continuous-histogram": {
    location: "the current place name",
    areaKm2: "the mapped area in km2",
    mean: "the mean value as a raw number",
    // year: "the chart year",
    // nodataAreaKm2: "the no-data area in km2",
    // meanCompliment: "1 minus the mean value as a raw number",
    // meanPercentage: "the mean value formatted as a percentage (intended for use when X-axis unit is %)",
    // meanComplimentPercentage: "1 minus the mean value formatted as a percentage (intended for use when X-axis unit is %)",
  },
} as const;

export type WidgetTemplateId = keyof typeof WIDGET_TEMPLATE_FIELDS;

export type WidgetTemplateValues<T extends WidgetTemplateId> = {
  [K in keyof (typeof WIDGET_TEMPLATE_FIELDS)[T]]: WidgetTemplateFieldValue;
};

export const getWidgetTemplateFields = (
  widgetType?: string | null,
  widgetChart?: string | null
) => {
  return WIDGET_TEMPLATE_FIELDS[`${widgetType}-${widgetChart}`];
};

export const normalizeBreakpointQuery = (query: string) =>
  query.replace(/^@media\s*/, "");

/**
 * Returns the config for the currently active MUI breakpoint.
 *
 * Pass an object keyed by breakpoint name, for example `{ xs: {...}, sm: {...} }`.
 * The hook returns `{}` when no configured breakpoint is active, when no config is
 * provided, or while rendering outside the browser.
 */
export const useBreakpointConfig = <T extends Record<string, any>>(
  breakpointConfig?: Record<string, T>
) => {
  const theme = useTheme();
  const [activeConfig, setActiveConfig] = React.useState<Partial<T>>({});

  React.useEffect(() => {
    if (typeof window === "undefined" || !breakpointConfig) {
      setActiveConfig({});
      return undefined;
    }

    const breakpointKeys = (
      theme.breakpoints?.keys ?? Object.keys(theme.breakpoints?.values ?? {})
    ).filter((key) => breakpointConfig[key]);

    const mediaQueries = breakpointKeys.map((key) => ({
      key,
      query: window.matchMedia(
        normalizeBreakpointQuery(theme.breakpoints.only(key as any))
      ),
    }));

    const updateActiveBreakpoint = () => {
      const activeBreakpoint = mediaQueries.find(
        ({ query }) => query.matches
      )?.key;
      setActiveConfig(
        activeBreakpoint ? breakpointConfig[activeBreakpoint] ?? {} : {}
      );
    };

    updateActiveBreakpoint();
    mediaQueries.forEach(({ query }) =>
      query.addEventListener("change", updateActiveBreakpoint)
    );

    return () => {
      mediaQueries.forEach(({ query }) =>
        query.removeEventListener("change", updateActiveBreakpoint)
      );
    };
  }, [theme, breakpointConfig]);

  return activeConfig;
};
